import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CINEMATIC_EASE, DURATIONS } from './motionConfig';
import useMagneticEffect from './useMagneticEffect';

const hoverTapTransition = {
  type: 'tween',
  duration: DURATIONS.micro,
  ease: CINEMATIC_EASE,
};

/**
 * Wraps a button or link. Magnetic offset + subtle hover scale + optional soft glow.
 * Children should be a single interactive element (button / Link).
 * Used on product cards (`ProductCard`) for add-to-cart FAB and footer CTAs — keep one motion layer (no duplicate scale on the child).
 */
export default function MagneticButton({ children, strength = 0.25, glow = true, className = '' }) {
  const reduced = useReducedMotion();
  const { ref, x, y } = useMagneticEffect({ strength, maxOffset: 7 });

  return (
    <motion.span
      ref={ref}
      style={{ x, y }}
      className={`group relative inline-flex max-w-full ${className}`}
      whileHover={reduced ? undefined : { scale: 1.03 }}
      whileTap={reduced ? undefined : { scale: 0.97 }}
      transition={hoverTapTransition}
    >
      {glow && !reduced && (
        <span
          className="pointer-events-none absolute -inset-3 -z-10 rounded-2xl bg-amber-400/25 opacity-0 blur-2xl transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:opacity-100"
          aria-hidden
        />
      )}
      <span className="relative inline-flex min-w-0 max-w-full">{children}</span>
    </motion.span>
  );
}
